import { z } from "zod";
import { colorSchema, dateOnlySchema, emailSchema, listQuerySchema, phoneSchema, timestampSchema, uuidSchema } from "./api.ts";
import {
  addressSchema,
  blankToNull,
  countryCodeSchema,
  hhmmSchema,
  httpUrlSchema,
  isUnique,
  queryBooleanSchema,
  timezoneSchema,
  userRefSchema,
  weekdaySchema,
} from "./common.ts";
import { DEFAULT_GST_RATE_BPS, financialYear, GST_INVOICE_NUMBER_REGEX, gstinSchema, gstStateCodeSchema, isValidGstin, panSchema } from "./india.ts";
import { CURRENCY_CODES, currencyCodeSchema, minorSchema } from "./money.ts";
import { PERMISSION_KEYS, permissionSchema, type Permission } from "./permissions.ts";

/**
 * Organization (workspace) kernel API:
 *   /api/v1/org, /api/v1/org/logo, /api/v1/org/transfer-ownership,
 *   /api/v1/members, /api/v1/invitations, /api/v1/roles, /api/v1/api-keys
 * Permissions: reads need an active membership; writes need `org:manage`,
 * `members:manage`, `roles:manage` or `integrations:manage` (API keys);
 * ownership transfer is owner-only.
 */

// ---------------------------------------------------------------------------
// Locale & calendar settings
// ---------------------------------------------------------------------------

export const DATE_FORMATS = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "DD-MM-YYYY", "DD MMM YYYY"] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];
export const dateFormatSchema = z.enum(DATE_FORMATS);
/** date-fns `format()` pattern for each org date format. */
export const DATE_FORMAT_PATTERNS: Record<DateFormat, string> = {
  "DD/MM/YYYY": "dd/MM/yyyy",
  "MM/DD/YYYY": "MM/dd/yyyy",
  "YYYY-MM-DD": "yyyy-MM-dd",
  "DD-MM-YYYY": "dd-MM-yyyy",
  "DD MMM YYYY": "dd MMM yyyy",
};

/** Number grouping locale (en-IN → 12,34,567.89; en-US → 1,234,567.89). */
export const NUMBER_LOCALES = ["en-IN", "en-US", "en-GB", "en-AU", "en-CA", "en-SG", "en-AE", "de-DE", "fr-FR", "es-ES", "nl-NL", "ja-JP"] as const;
export type NumberLocale = (typeof NUMBER_LOCALES)[number];
export const numberLocaleSchema = z.enum(NUMBER_LOCALES);

/** Default working days/hours (used for capacity, SLAs and due-date maths). */
export const workingHoursSchema = z.object({
  days: z.array(weekdaySchema).min(1).max(7),
  start: hhmmSchema,
  end: hhmmSchema,
});
export type WorkingHours = z.infer<typeof workingHoursSchema>;
export const workingHoursInputSchema = workingHoursSchema
  .strict()
  .refine((w) => isUnique(w.days), { message: "Duplicate working day", path: ["days"] })
  .refine((w) => w.end > w.start, { message: "End must be after start", path: ["end"] });

// ---------------------------------------------------------------------------
// Invoice settings (GST-aware)
// ---------------------------------------------------------------------------

export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const SWIFT_REGEX = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
export const UPI_ID_REGEX = /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$/;
/** Document-number prefix: GST allows only A–Z, 0–9, "-" and "/" (whole number ≤ 16 chars). */
export const DOCUMENT_PREFIX_REGEX = /^[A-Za-z0-9\-/]{0,10}$/;

export const bankDetailsSchema = z.object({
  accountName: z.string().nullable(),
  accountNumber: z.string().nullable(),
  ifsc: z.string().nullable(),
  bankName: z.string().nullable(),
  branch: z.string().nullable(),
  upiId: z.string().nullable(),
  swift: z.string().nullable(),
});
export type BankDetails = z.infer<typeof bankDetailsSchema>;

export const bankDetailsInputSchema = z
  .object({
    accountName: blankToNull(z.string().max(120)),
    /** Account number or IBAN. */
    accountNumber: blankToNull(z.string().regex(/^[A-Za-z0-9]{6,34}$/, "Invalid account number")),
    ifsc: blankToNull(z.string().toUpperCase().regex(IFSC_REGEX, "Invalid IFSC")),
    bankName: blankToNull(z.string().max(120)),
    branch: blankToNull(z.string().max(120)),
    upiId: blankToNull(z.string().regex(UPI_ID_REGEX, "Invalid UPI ID")),
    swift: blankToNull(z.string().toUpperCase().regex(SWIFT_REGEX, "Invalid SWIFT/BIC")),
  })
  .partial()
  .strict();

/**
 * Invoice numbering: `${prefix}${includeFyInNumber ? "26-27/" : ""}${seq padded}`,
 * e.g. "INV/26-27/0001". `nextNumber` is backed by the kernel document-sequence
 * service (per org, per document kind, per FY; allocated with a row lock inside
 * the issuing transaction). `nextNumberPreview` is computed, read-only.
 */
export const invoiceSettingsSchema = z.object({
  prefix: z.string(),
  creditNotePrefix: z.string(),
  includeFyInNumber: z.boolean(),
  numberPadding: z.number().int(),
  nextNumber: z.number().int(),
  nextNumberPreview: z.string(),
  defaultDueDays: z.number().int(),
  defaultNotes: z.string().nullable(),
  defaultTerms: z.string().nullable(),
  showAmountInWords: z.boolean(),
  /** 6-digit SAC used for new line items (null = none). */
  defaultSacCode: z.string().nullable(),
  /** Default GST rate in basis points (1800 = 18%). */
  defaultTaxRateBps: z.number().int(),
  /** Letter of Undertaking number for zero-rated export invoices. */
  lutNumber: z.string().nullable(),
  lutValidTill: dateOnlySchema.nullable(),
  bankDetails: bankDetailsSchema,
});
export type InvoiceSettings = z.infer<typeof invoiceSettingsSchema>;

export const INVOICE_SETTINGS_DEFAULTS = {
  prefix: "INV/",
  creditNotePrefix: "CN/",
  includeFyInNumber: true,
  numberPadding: 4,
  nextNumber: 1,
  defaultDueDays: 15,
  defaultNotes: null,
  defaultTerms: null,
  showAmountInWords: true,
  defaultSacCode: "998361",
  defaultTaxRateBps: DEFAULT_GST_RATE_BPS,
  lutNumber: null,
  lutValidTill: null,
} as const;

/** PATCH /api/v1/org → invoiceSettings (merged key by key; bankDetails merged too). */
export const invoiceSettingsInputSchema = z
  .object({
    prefix: z.string().trim().regex(DOCUMENT_PREFIX_REGEX, "Up to 10 chars: letters, digits, - and /"),
    creditNotePrefix: z.string().trim().regex(DOCUMENT_PREFIX_REGEX, "Up to 10 chars: letters, digits, - and /"),
    includeFyInNumber: z.boolean(),
    numberPadding: z.number().int().min(1).max(8),
    /** Next sequence for the current FY. Rejected with CONFLICT if ≤ the last issued number. */
    nextNumber: z.number().int().min(1).max(99_999_999),
    defaultDueDays: z.number().int().min(0).max(365),
    defaultNotes: blankToNull(z.string().max(2000)),
    defaultTerms: blankToNull(z.string().max(5000)),
    showAmountInWords: z.boolean(),
    defaultSacCode: blankToNull(z.string().regex(/^\d{4,8}$/, "SAC/HSN must be 4–8 digits")),
    defaultTaxRateBps: z.number().int().min(0).max(10_000),
    lutNumber: blankToNull(z.string().max(50)),
    lutValidTill: dateOnlySchema.nullable(),
    bankDetails: bankDetailsInputSchema,
  })
  .partial()
  .strict();

/** Short FY label used inside document numbers: "2026-27" → "26-27", "2026" → "26". */
export function shortFinancialYear(date: Date | string, fyStartMonth = 4): string {
  const fy = financialYear(date, fyStartMonth);
  return fy.length === 7 ? `${fy.slice(2, 4)}-${fy.slice(5)}` : fy.slice(2);
}

/** Pure: builds a document number (invoice / credit note) from settings + sequence. */
export function formatDocumentNumber(opts: {
  prefix: string;
  includeFy: boolean;
  padding: number;
  sequence: number;
  date: Date | string;
  fyStartMonth: number;
}): string {
  const seq = String(opts.sequence).padStart(opts.padding, "0");
  const fy = opts.includeFy ? `${shortFinancialYear(opts.date, opts.fyStartMonth)}/` : "";
  return `${opts.prefix}${fy}${seq}`;
}

// ---------------------------------------------------------------------------
// "Ask your business" AI (OpenAI) settings
// ---------------------------------------------------------------------------

/**
 * The AI assistant uses OpenAI through the `openai` SDK — either OpenAI directly
 * (env OPENAI_API_KEY, OPENAI_MODEL, optional OPENAI_BASE_URL) or Azure OpenAI
 * (env AZURE_OPENAI_ENDPOINT, AZURE_OPENAI_API_KEY, AZURE_OPENAI_DEPLOYMENT,
 * AZURE_OPENAI_API_VERSION). Provider/model are deployment config (env), never
 * org-editable; orgs only control whether and what data may be sent.
 */
export const AI_PROVIDERS = ["openai", "azure_openai"] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];
export const aiProviderSchema = z.enum(AI_PROVIDERS);

/** Org-level data-governance switches for the AI assistant (DPDP/GDPR friendly). */
export const aiSettingsSchema = z.object({
  /** Master switch; users additionally need the `ai:use` permission. */
  enabled: z.boolean(),
  /** Allow monetary figures to be sent to the model (users still need `financials:view`). */
  allowFinancialData: z.boolean(),
  /** Allow contact PII (emails, phones, addresses) to be sent; otherwise masked before the call. */
  allowPersonalData: z.boolean(),
  /** How long AI conversations are kept (0 = not stored). */
  conversationRetentionDays: z.number().int(),
});
export type AiSettings = z.infer<typeof aiSettingsSchema>;
export const AI_SETTINGS_DEFAULTS: AiSettings = { enabled: true, allowFinancialData: true, allowPersonalData: false, conversationRetentionDays: 90 };

export const aiSettingsInputSchema = z
  .object({
    enabled: z.boolean(),
    allowFinancialData: z.boolean(),
    allowPersonalData: z.boolean(),
    conversationRetentionDays: z.number().int().min(0).max(3650),
  })
  .partial()
  .strict();

/** Read-only, derived from server env: lets the web show "AI not configured". */
export const aiStatusSchema = z.object({
  provider: aiProviderSchema.nullable(),
  configured: z.boolean(),
  /** OpenAI model name or Azure deployment name. */
  model: z.string().nullable(),
});
export type AiStatus = z.infer<typeof aiStatusSchema>;

// ---------------------------------------------------------------------------
// Organization settings — GET / PATCH /api/v1/org
// ---------------------------------------------------------------------------

/** GET /api/v1/org → OrgSettings (also returned by PATCH). */
export const orgSettingsSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  legalName: z.string().nullable(),
  /** Read-only (used in portal URLs). */
  slug: z.string(),
  logoUrl: z.string().nullable(),
  brandColor: z.string().nullable(),
  country: z.string(),
  /** GST state code of the principal place of business (India). */
  stateCode: z.string().nullable(),
  gstin: z.string().nullable(),
  pan: z.string().nullable(),
  address: addressSchema,
  phone: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  baseCurrency: currencyCodeSchema,
  enabledCurrencies: z.array(currencyCodeSchema),
  timezone: z.string(),
  /** 1–12; 4 = April (Indian FY Apr–Mar). */
  fyStartMonth: z.number().int().min(1).max(12),
  dateFormat: dateFormatSchema,
  numberLocale: numberLocaleSchema,
  weekStartsOn: weekdaySchema,
  workingHours: workingHoursSchema,
  invoiceSettings: invoiceSettingsSchema,
  ai: aiSettingsSchema,
  aiStatus: aiStatusSchema,
  plan: z.string(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type OrgSettings = z.infer<typeof orgSettingsSchema>;

/**
 * PATCH /api/v1/org (perm `org:manage`) → OrgSettings.
 * Merge-patch: omitted = unchanged; null/"" clears nullable fields; `address` and
 * `workingHours` are replaced as a whole; `invoiceSettings`, `bankDetails`, `ai`
 * are merged key by key. `logoUrl` accepts only null (upload via POST /org/logo).
 * After merging, the server runs `validateOrgSettings()` and answers
 * VALIDATION_FAILED with `details: OrgValidationIssue[]`. Changing
 * `baseCurrency` once financial documents exist → CONFLICT.
 */
export const updateOrgInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    legalName: blankToNull(z.string().max(200)),
    logoUrl: z.null(),
    brandColor: colorSchema.nullable(),
    country: countryCodeSchema,
    stateCode: blankToNull(gstStateCodeSchema),
    gstin: blankToNull(gstinSchema),
    pan: blankToNull(panSchema),
    address: addressSchema.strict(),
    phone: blankToNull(phoneSchema),
    email: blankToNull(emailSchema),
    website: blankToNull(httpUrlSchema),
    baseCurrency: currencyCodeSchema,
    enabledCurrencies: z
      .array(currencyCodeSchema)
      .min(1)
      .max(CURRENCY_CODES.length)
      .refine(isUnique, "Duplicate currency"),
    timezone: timezoneSchema,
    fyStartMonth: z.number().int().min(1).max(12),
    dateFormat: dateFormatSchema,
    numberLocale: numberLocaleSchema,
    weekStartsOn: weekdaySchema,
    workingHours: workingHoursInputSchema,
    invoiceSettings: invoiceSettingsInputSchema,
    ai: aiSettingsInputSchema,
    /**
     * Re-authentication for a sensitive change.
     *
     * Required only when the patch touches the bank account, IFSC or UPI id
     * printed on invoices. Changing those silently is the invoice-fraud
     * pattern: the agency keeps invoicing, the clients keep paying, and the
     * money goes elsewhere. The server decides when these are needed; sending
     * them when they are not is harmless.
     */
    password: z.string().min(1).optional(),
    totpCode: z.string().min(6).max(10).optional(),
  })
  .partial()
  .strict();
export type UpdateOrgInput = z.input<typeof updateOrgInputSchema>;
export type UpdateOrgData = z.output<typeof updateOrgInputSchema>;

export interface OrgValidationIssue {
  /** Dotted path, e.g. "gstin" or "invoiceSettings.prefix". */
  path: string;
  message: string;
}

/**
 * Pure cross-field checks on the MERGED org state (server after merge-patch;
 * web on the whole form): GSTIN ↔ country/state/PAN, base ∈ enabled currencies,
 * invoice numbers within the 16-char GST limit for Indian orgs.
 */
export function validateOrgSettings(s: {
  country: string;
  stateCode: string | null;
  gstin: string | null;
  pan: string | null;
  baseCurrency: string;
  enabledCurrencies: readonly string[];
  fyStartMonth: number;
  invoiceSettings: { prefix: string; creditNotePrefix: string; includeFyInNumber: boolean; numberPadding: number };
}): OrgValidationIssue[] {
  const issues: OrgValidationIssue[] = [];
  const isIndia = s.country.toUpperCase() === "IN";
  if (s.gstin) {
    const gstin = s.gstin.toUpperCase();
    if (!isIndia) issues.push({ path: "gstin", message: "GSTIN applies only to organizations in India" });
    if (!isValidGstin(gstin)) issues.push({ path: "gstin", message: "Invalid GSTIN" });
    if (s.stateCode && gstin.slice(0, 2) !== s.stateCode) issues.push({ path: "stateCode", message: "State does not match the GSTIN state code" });
    if (s.pan && gstin.slice(2, 12) !== s.pan.toUpperCase()) issues.push({ path: "pan", message: "PAN does not match the GSTIN" });
  }
  if (isIndia && s.gstin && !s.stateCode) issues.push({ path: "stateCode", message: "Select the GST state" });
  if (!s.enabledCurrencies.includes(s.baseCurrency)) issues.push({ path: "enabledCurrencies", message: "Enabled currencies must include the base currency" });
  if (isIndia) {
    const inv = s.invoiceSettings;
    const widest = 10 ** inv.numberPadding - 1;
    const common = { includeFy: inv.includeFyInNumber, padding: inv.numberPadding, sequence: widest, date: new Date(), fyStartMonth: s.fyStartMonth };
    for (const [key, prefix] of [["prefix", inv.prefix], ["creditNotePrefix", inv.creditNotePrefix]] as const) {
      const sample = formatDocumentNumber({ ...common, prefix });
      if (!GST_INVOICE_NUMBER_REGEX.test(sample)) {
        issues.push({ path: `invoiceSettings.${key}`, message: `"${sample}" exceeds the GST limit of 16 characters (A–Z, 0–9, - and /)` });
      }
    }
  }
  return issues;
}

/** POST /api/v1/org/logo — multipart, single field "file" (≤ 2 MB). SVGs are sanitized. */
export const ORG_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const ORG_LOGO_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"] as const;
/** POST /api/v1/org/logo → { logoUrl } (publicly readable, cache-busted URL). */
export const uploadLogoResultSchema = z.object({ logoUrl: z.string() });
export type UploadLogoResult = z.infer<typeof uploadLogoResultSchema>;

// ---------------------------------------------------------------------------
// Roles — /api/v1/roles
// ---------------------------------------------------------------------------

export const roleRefSchema = z.object({
  id: uuidSchema,
  /** System key ("owner", "admin", …) or a server-generated slug for custom roles. */
  key: z.string(),
  name: z.string(),
  isSystem: z.boolean(),
});
export type RoleRef = z.infer<typeof roleRefSchema>;

/** Role DTO. System roles are immutable and cannot be deleted. */
export const roleSchema = z.object({
  id: uuidSchema,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  /** Unknown/retired keys are filtered out by the server. */
  permissions: z.array(permissionSchema),
  memberCount: z.number().int(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Role = z.infer<typeof roleSchema>;

const permissionListSchema = z.array(permissionSchema).max(PERMISSION_KEYS.length).refine(isUnique, "Duplicate permission");

const roleFieldsShape = {
  name: z.string().trim().min(1).max(60),
  description: blankToNull(z.string().max(300)),
  permissions: permissionListSchema,
};

/** POST /api/v1/roles (perm `roles:manage`) → 201 Role. Duplicate name → CONFLICT. */
export const createRoleInputSchema = z
  .object({ ...roleFieldsShape, description: roleFieldsShape.description.optional() })
  .strict();
export type CreateRoleInput = z.input<typeof createRoleInputSchema>;

/** PATCH /api/v1/roles/:id (perm `roles:manage`) → Role. System role → FORBIDDEN. */
export const updateRoleInputSchema = z.object(roleFieldsShape).partial().strict();
export type UpdateRoleInput = z.input<typeof updateRoleInputSchema>;

/**
 * DELETE /api/v1/roles/:id?reassignToRoleId= (perm `roles:manage`) → 204.
 * System roles → FORBIDDEN. Members still assigned and no reassignment → CONFLICT.
 */
export const deleteRoleQuerySchema = z.object({ reassignToRoleId: uuidSchema.optional() });

/** GET /api/v1/roles → { items: Role[] } (system roles first, then custom by name). */

/** Grouping of the permission catalogue for the role editor UI (covers every key). */
export const PERMISSION_GROUPS: readonly { key: string; label: string; permissions: readonly Permission[] }[] = [
  { key: "workspace", label: "Workspace administration", permissions: ["org:manage", "members:manage", "roles:manage", "billing:manage", "integrations:manage", "audit:view", "data:export", "data:import"] },
  { key: "sales", label: "Sales", permissions: ["leads:read", "leads:write", "leads:delete", "leads:convert", "pipeline:manage"] },
  { key: "clients", label: "Clients", permissions: ["clients:read", "clients:write", "clients:delete"] },
  { key: "proposals", label: "Proposals & contracts", permissions: ["proposals:read", "proposals:write", "contracts:read", "contracts:write", "contracts:sign"] },
  { key: "finance", label: "Finance", permissions: ["financials:view", "invoices:read", "invoices:write", "invoices:send", "payments:record", "expenses:read", "expenses:write", "expenses:approve"] },
  { key: "delivery", label: "Delivery", permissions: ["projects:read", "projects:read_all", "projects:write", "projects:delete", "tasks:write", "time:track", "time:manage", "templates:manage"] },
  { key: "collaboration", label: "Collaboration", permissions: ["files:read", "files:write", "portal:manage", "messages:write", "calendar:write"] },
  { key: "intelligence", label: "Intelligence & automation", permissions: ["reports:view", "ai:use", "automations:manage", "forms:manage"] },
];

// ---------------------------------------------------------------------------
// Members — /api/v1/members
// ---------------------------------------------------------------------------

export const MEMBER_STATUSES = ["active", "suspended"] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];
export const memberStatusSchema = z.enum(MEMBER_STATUSES);

/**
 * Member DTO (`id` is the membership id, not the user id). Rates are per hour in
 * the org base currency and are null when the viewer lacks `financials:view`.
 */
export const memberSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  role: roleRefSchema,
  isOwner: z.boolean(),
  status: memberStatusSchema,
  title: z.string().nullable(),
  costRateMinor: minorSchema.nullable(),
  billRateMinor: minorSchema.nullable(),
  capacityMinutesPerWeek: z.number().int(),
  twoFactorEnabled: z.boolean(),
  lastActiveAt: timestampSchema.nullable(),
  joinedAt: timestampSchema,
});
export type Member = z.infer<typeof memberSchema>;

/**
 * GET /api/v1/members?q&status&roleId&limit&offset&sort&order → Page<Member>.
 * sort ∈ name | email | joinedAt | lastActiveAt (default name asc).
 * GET /api/v1/members/:id → Member.
 */
export const listMembersQuerySchema = listQuerySchema.extend({
  status: memberStatusSchema.optional(),
  roleId: uuidSchema.optional(),
});
export type ListMembersQuery = z.infer<typeof listMembersQuerySchema>;

/**
 * PATCH /api/v1/members/:id (perm `members:manage`) → Member.
 * Cannot assign the `owner` role (use transfer-ownership), cannot suspend or
 * change the owner, cannot remove the last admin-capable member → FORBIDDEN/CONFLICT.
 */
export const updateMemberInputSchema = z
  .object({
    roleId: uuidSchema,
    title: blankToNull(z.string().max(100)),
    costRateMinor: minorSchema.min(0).nullable(),
    billRateMinor: minorSchema.min(0).nullable(),
    /** 0–10080 (minutes in a week); default 2400 = 40 h. */
    capacityMinutesPerWeek: z.number().int().min(0).max(10_080),
    status: memberStatusSchema,
    /**
     * The caller's own password, re-entered.
     *
     * Required only when the change GRANTS workspace administration — the
     * server decides, because only it knows what a role carries. Sent on every
     * save by the members screen, which is simpler than asking the browser to
     * work out whether this particular role is privileged.
     */
    password: z.string().min(1).max(200).optional(),
    totpCode: z.string().min(6).max(10).optional(),
  })
  .partial()
  .strict()
  .refine((o) => Object.keys(o).filter((k) => k !== "password" && k !== "totpCode").length > 0, "Nothing to update");
export type UpdateMemberInput = z.input<typeof updateMemberInputSchema>;

/** DELETE /api/v1/members/:id (perm `members:manage`) → 204. Owner → FORBIDDEN. Sessions are revoked. */

export const DEFAULT_CAPACITY_MINUTES_PER_WEEK = 2400;

// ---------------------------------------------------------------------------
// Invitations — /api/v1/invitations
// ---------------------------------------------------------------------------

export const INVITATION_TTL_DAYS = 7;
export const INVITATION_RESEND_COOLDOWN_SECONDS = 60;
export const INVITATION_STATUSES = ["pending", "accepted", "revoked", "expired"] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];
export const invitationStatusSchema = z.enum(INVITATION_STATUSES);

export const invitationSchema = z.object({
  id: uuidSchema,
  email: z.string(),
  role: roleRefSchema,
  status: invitationStatusSchema,
  message: z.string().nullable(),
  invitedBy: userRefSchema.nullable(),
  expiresAt: timestampSchema,
  lastSentAt: timestampSchema,
  sendCount: z.number().int(),
  acceptedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type Invitation = z.infer<typeof invitationSchema>;

/** GET /api/v1/invitations?status= (perm `members:manage`) → { items: Invitation[] } (default status pending). */
export const listInvitationsQuerySchema = z.object({ status: invitationStatusSchema.optional() });

/**
 * POST /api/v1/invitations (perm `members:manage`) → 201 Invitation.
 * Already a member or a pending invite exists → CONFLICT. Owner role → FORBIDDEN.
 */
export const createInvitationInputSchema = z
  .object({
    email: emailSchema,
    roleId: uuidSchema,
    message: blankToNull(z.string().max(500)).optional(),
  })
  .strict();
export type CreateInvitationInput = z.input<typeof createInvitationInputSchema>;

/**
 * POST /api/v1/invitations/:id/resend → Invitation (new token, expiry reset;
 * within cooldown → RATE_LIMITED). DELETE /api/v1/invitations/:id → 204 (revoke).
 */

// ---------------------------------------------------------------------------
// API keys — /api/v1/api-keys  (perm `integrations:manage`)
// ---------------------------------------------------------------------------

/** Full token: `l2a_sk_<prefix>_<secret>`; prefix = 12 [a-z0-9] (stored plain, unique), secret = 32–64 [A-Za-z0-9] (stored as SHA-256). */
export const API_KEY_TOKEN_PREFIX = "l2a_sk_";
export const API_KEY_REGEX = /^l2a_sk_([a-z0-9]{12})_([A-Za-z0-9]{32,64})$/;

export function parseApiKey(token: string): { prefix: string; secret: string } | null {
  const m = API_KEY_REGEX.exec(token.trim());
  if (!m || !m[1] || !m[2]) return null;
  return { prefix: m[1], secret: m[2] };
}
export function formatApiKey(prefix: string, secret: string): string {
  return `${API_KEY_TOKEN_PREFIX}${prefix}_${secret}`;
}
/** Display form, e.g. "l2a_sk_ab12cd34ef56_••••". */
export function maskApiKey(prefix: string): string {
  return `${API_KEY_TOKEN_PREFIX}${prefix}_••••`;
}

export const API_KEY_STATUSES = ["active", "expired", "revoked"] as const;
export const apiKeyStatusSchema = z.enum(API_KEY_STATUSES);

export const apiKeySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  prefix: z.string(),
  maskedKey: z.string(),
  permissions: z.array(permissionSchema),
  status: apiKeyStatusSchema,
  createdBy: userRefSchema.nullable(),
  lastUsedAt: timestampSchema.nullable(),
  lastUsedIp: z.string().nullable(),
  expiresAt: timestampSchema.nullable(),
  revokedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type ApiKey = z.infer<typeof apiKeySchema>;

/** GET /api/v1/api-keys?includeRevoked= → { items: ApiKey[] }. */
export const listApiKeysQuerySchema = z.object({ includeRevoked: queryBooleanSchema.optional() });

/**
 * POST /api/v1/api-keys → 201 CreateApiKeyResult. Permissions must be a subset
 * of the creator's own permissions (else FORBIDDEN). `expiresAt` must be in the future.
 */
export const createApiKeyInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    permissions: permissionListSchema.min(1),
    expiresAt: timestampSchema.nullable().optional(),
    /**
     * The caller's own password, re-entered.
     *
     * An API key reads and writes this workspace with no browser session and
     * no second factor, and its secret is shown once. Somebody who has borrowed
     * a signed-in tab should not be able to walk out with a permanent one.
     */
    password: z.string().min(1).max(200),
    totpCode: z.string().min(6).max(10).optional(),
  })
  .strict()
  .refine((o) => !o.expiresAt || Date.parse(o.expiresAt) > Date.now(), { message: "Expiry must be in the future", path: ["expiresAt"] });
export type CreateApiKeyInput = z.input<typeof createApiKeyInputSchema>;

/** `secret` is the full `l2a_sk_<prefix>_<secret>` token — shown ONCE, never retrievable again. */
export const createApiKeyResultSchema = z.object({ apiKey: apiKeySchema, secret: z.string() });
export type CreateApiKeyResult = z.infer<typeof createApiKeyResultSchema>;

/** DELETE /api/v1/api-keys/:id → 204 (revokes; row kept for audit). */

// ---------------------------------------------------------------------------
// Ownership transfer — POST /api/v1/org/transfer-ownership (owner only)
// ---------------------------------------------------------------------------

/**
 * Re-authenticates the current owner (password, + TOTP when 2FA is on). Target
 * must be an active member. The previous owner becomes `admin`.
 */
export const transferOwnershipInputSchema = z
  .object({
    userId: uuidSchema,
    password: z.string().min(1).max(128),
    totpCode: z.string().regex(/^\d{6}$/).optional(),
  })
  .strict();
export type TransferOwnershipInput = z.infer<typeof transferOwnershipInputSchema>;

export const transferOwnershipResultSchema = z.object({
  newOwner: userRefSchema,
  previousOwnerRole: roleRefSchema,
});
export type TransferOwnershipResult = z.infer<typeof transferOwnershipResultSchema>;

// ---------------------------------------------------------------------------
// Send from your own domain (GET / PATCH /api/v1/org/mail)
// ---------------------------------------------------------------------------

/**
 * What the API returns about a workspace's own mail server.
 *
 * The password is never in this shape, in any form. `hasPassword` is all the
 * settings screen needs to show "configured", and `verifiedAt` is set only
 * when a real message has gone through — saving is not evidence of anything.
 */
export const orgMailSchema = z.object({
  host: z.string().nullable(),
  port: z.number().int().nullable(),
  secure: z.boolean(),
  user: z.string().nullable(),
  hasPassword: z.boolean(),
  from: z.string().nullable(),
  replyTo: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  /** Host and sender are both set, so the mailer would use these. */
  ready: z.boolean(),
  /** False when the plan does not include `own_smtp`; the form is read-only. */
  entitled: z.boolean(),
  /** What clients actually see in the From line today. */
  effectiveFrom: z.string(),
});
export type OrgMail = z.infer<typeof orgMailSchema>;

export const updateOrgMailInputSchema = z
  .object({
    host: blankToNull(z.string().max(253)),
    port: z.number().int().min(1).max(65535).nullable(),
    secure: z.boolean(),
    user: blankToNull(z.string().max(320)),
    /** Write-only. Send null to clear it; omit it to leave it alone. */
    password: z.string().max(400).nullable(),
    from: blankToNull(z.string().max(200)),
    replyTo: blankToNull(z.string().max(200)),
    /**
     * The caller's OWN password, re-entered.
     *
     * Named apart from `password` above because the two are easy to confuse
     * and confusing them would send an account password to a third-party
     * relay. One is the SMTP credential; this is proof of who is typing.
     */
    password_confirm: z.string().min(1).max(200),
    totpCode: z.string().min(6).max(10).optional(),
  })
  .partial({ host: true, port: true, secure: true, user: true, password: true, from: true, replyTo: true, totpCode: true })
  .strict();
export type UpdateOrgMailInput = z.input<typeof updateOrgMailInputSchema>;

export const testOrgMailInputSchema = z.object({ to: z.string().email() }).strict();

export const testOrgMailResultSchema = z.object({
  ok: z.boolean(),
  /** The relay's own refusal, verbatim, so the fix is obvious. */
  error: z.string().nullable(),
  verifiedAt: z.string().nullable(),
});
